/*
 * Formularul „Cerere": un tichet liber, pentru ce nu e in catalog.
 *
 * Trei campuri - tip, subiect, descriere - si atat.
 *
 * Dupa trimitere se pleaca DE AICI, pe fisa tichetului din "Cererile mele".
 * Ecranul asta avea inainte o pagina proprie de confirmare, cu numarul si trei
 * butoane, fiindca tichetele directe nu apareau in lista si omul n-ar fi gasit
 * unde sa se uite. De cand apar acolo, pagina aia era un popas in plus intre om
 * si singurul loc unde chiar scrie ce se intampla cu tichetul lui: stare, cine
 * l-a luat, comentarii.
 *
 * Tipurile vin din api si sunt doar cele care chiar pot naste un tichet -
 * active si cu flux. Un formular care ar arata toate tipurile ar promite
 * tichete pe care ruta de trimitere le refuza.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as shop from '../api/shop';
import { ApiError } from '../api/errors';
import { ArticleCard } from '../components/ArticleCard';
import { SelfServeSearch, type SearchState } from '../components/SelfServeSearch';
import { ServiceRow } from '../components/ServiceRow';
import { useToasts } from '../components/ToastContext';
import { EmptyState, ErrorState, InlineAlert, Loading } from '../components/states';
import { useAsync } from '../lib/useAsync';
import { t } from '../lib/i18n';

export function NewTicketScreen() {
  const types = useAsync((signal) => shop.ticketTypes({ signal }), []);
  const { push } = useToasts();
  const navigate = useNavigate();

  /*
   * Venit de la un articol care n-a ajutat - vezi HelpArticleScreen.
   *
   * `q` e titlul articolului: intra in caseta, deci si in subiect, si cauta
   * pe loc serviciile din catalog care i se potrivesc. `article` scoate
   * articolul din rezultate - tocmai l-a citit - si deschide formularul fara
   * sa mai astepte o cautare. Articolele nu sunt legate inca de un tip de
   * tichet sau de un serviciu, deci tipul ramane cel de mai jos.
   */
  const [params] = useSearchParams();
  const fromArticle = Number(params.get('article')) || 0;
  const initialQuery = (params.get('q') ?? '').trim().slice(0, 255);
  /* Cautarea care l-a dus la articol, daca a fost una: ea stie ce a incercat
     omul inainte, nu cautarea refacuta aici din titlu. */
  const carriedSearchId =
    Number((useLocation().state as { searchId?: unknown } | null)?.searchId) || 0;

  const [typeId, setTypeId] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /* Numele campurilor din raspunsul de validare al api-ului: type_id, subject,
     description. Aceleasi chei si aici, ca eroarea sa se aseze langa campul ei. */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /*
   * Ce a cautat omul in caseta de deasupra.
   *
   * Ecranul asta decide pe baza ei DACA arata formularul de cerere. Caseta nu
   * stie nimic despre formular - ea raspunde la o intrebare, iar ce se face cu
   * raspunsul e treaba de aici.
   *
   * `useCallback` fiindca il primeste caseta ca prop si il are in `deps`: o
   * functie noua la fiecare redesenare ar fi pornit un ciclu.
   */
  const [search, setSearch] = useState<SearchState>({
    searchId: 0,
    query: initialQuery,
    searched: false,
    found: false,
    articles: [],
    services: [],
  });
  const rememberSearch = useCallback((state: SearchState) => setSearch(state), []);

  /*
   * Subiectul cererii e chiar ce a scris omul in caseta.
   *
   * Nu i se cere a doua oara acelasi lucru cu alte cuvinte: tocmai a descris
   * problema sus, iar un al doilea camp gol numit "Subiect" l-ar pune sa se
   * repete si i-ar da senzatia ca prima scriere n-a folosit la nimic.
   *
   * Se suprascrie doar cat timp omul nu l-a schimbat el: daca l-a corectat, o
   * cautare noua nu are voie sa-i stearga corectura.
   */
  const [subjectTouched, setSubjectTouched] = useState(false);

  useEffect(() => {
    if (!subjectTouched) setSubject(search.query);
  }, [search.query, subjectTouched]);

  /*
   * Tipul de tichet se precompleteaza cu primul din lista.
   *
   * Api-ul intoarce numai tipurile care chiar pot naste un tichet aici, deci
   * oricare dintre ele merge; primul e o alegere arbitrara, dar VIZIBILA si
   * schimbabila, nu una ascunsa. Un implicit adevarat - ales de furnizor in
   * portal - inca nu exista, si acolo ii e locul.
   */
  const [typeTouched, setTypeTouched] = useState(false);
  const firstType = types.data?.types[0]?.id;

  useEffect(() => {
    if (!typeTouched && typeId === '' && firstType !== undefined) {
      setTypeId(String(firstType));
    }
  }, [firstType, typeTouched, typeId]);

  /*
   * CELE DOUA COLOANE SE DESCHID CAND S-A CAUTAT, NU MAI DEVREME.
   *
   * Pe un ecran gol nu e nimic de ales: nici raspunsuri de citit, nici subiect
   * de pus pe cerere - subiectul vine chiar din caseta. Un formular deschis
   * peste nimic spune "oricum o sa deschizi tichet", si atunci nimeni nu mai
   * citeste nimic.
   *
   * NU e acelasi lucru cu vechea regula, care ascundea formularul PANA CAND
   * cautarea esua. Aia il facea o pedeapsa pentru cine n-a gasit; asta il aduce
   * odata cu raspunsurile, ca a treia varianta pusa alaturi de ele. Omul care
   * scrie ceva le primeste pe toate deodata si alege.
   *
   * `searched` si nu "a scris ceva": asa formularul si rezultatele apar in
   * ACEEASI clipa. Legat de prima tasta, ar fi rasarit singur, cu o coloana
   * goala langa el vreo jumatate de secunda. Cand cautarea cade, `searched` e
   * tot adevarat - vezi caseta - deci un api mut nu inchide iesirea.
   */
  const otherArticles = search.articles.filter((article) => article.id !== fromArticle);
  const asked = search.searched || fromArticle > 0;
  const nothingFound = search.searched && otherArticles.length === 0 && search.services.length === 0;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    /* Drumul scurt, langa camp. Api-ul verifica oricum - vezi
       TaskFulfilment::openDirect() - asta doar scuteste o cerere. */
    const missing: Record<string, string> = {};
    if (typeId === '') missing.type_id = t('Choose a ticket type.');
    if (subject.trim() === '') missing.subject = t('Write a short subject.');
    if (description.trim() === '') missing.description = t('Describe what you need.');

    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      return;
    }

    setFieldErrors({});
    setSubmitting(true);

    try {
      /* Numarul cautarii pleaca CU tichetul, nu dupa el.
         Api-ul scrie din el nota interna cu ce a incercat omul, si tot el
         noteaza in jurnal ca s-a ajuns la tichet. Inainte masura pleca
         separat, dupa raspuns, si cine inchidea fila intre cele doua cereri
         lasa in urma un tichet deschis si un jurnal care zicea ca a cautat si
         n-a facut nimic. */
      const result = await shop.createTicket(
        Number(typeId),
        subject.trim(),
        description.trim(),
        carriedSearchId > 0 ? carriedSearchId : search.searchId,
      );

      push(t('Ticket opened.'));

      /*
       * Direct pe fisa tichetului, si cu `replace`.
       *
       * `replace` fiindca formularul asta si-a facut treaba: lasat in istoric,
       * un "inapoi" l-ar fi adus la loc, cu campurile golite de remontare, si
       * cel mai firesc lucru de facut acolo ar fi fost sa trimita a doua oara
       * acelasi tichet.
       */
      navigate(`/tickets/${result.ticket_id}`, { replace: true });
    } catch (cause) {
      if (cause instanceof ApiError && cause.fields) {
        setFieldErrors(cause.fields);
      } else {
        setSubmitError(cause instanceof Error ? cause.message : t('The ticket could not be opened.'));
      }
      setSubmitting(false);
    }
  };

  return (
    <>
      <header className="page-head">
        <div>
          <p className="micro-label">{t('Your request')}</p>
          {/* Întrebarea o pune titlul, o singură dată, si el eticheteaza caseta
              de dedesubt. Doua intrebari una sub alta se citesc ca o scapare. */}
          <h1 className="page-title" id="page-question">{t('How can we help you today?')}</h1>
        </div>
      </header>

      {/*
        * Caseta e primul si singurul lucru de pe ecran, pana scrie ceva.
        *
        * In afara ramurii care asteapta tipurile de tichet: cautarea n-are
        * nicio treaba cu ele, iar daca api-ul de tipuri cade, scurtatura care
        * l-ar fi scutit de tichet trebuie sa mearga mai departe.
        */}
      <SelfServeSearch
        key={initialQuery}
        onSearch={rememberSearch}
        labelledBy="page-question"
        initialQuery={initialQuery}
      />

      {/*
        * Doua coloane: in stanga se rezolva singur, in dreapta se inregistreaza.
        *
        * Amandoua deodata, nu una dupa ce cealalta esueaza: un ecran care
        * ascunde iesirea pana cand nu gasesti nimic te tine ostatic, iar unul
        * care ascunde raspunsurile sub un formular deschis spune "oricum o sa
        * deschizi tichet". Alaturi, omul le vede pe amandoua si alege.
        *
        * Dar amandoua abia DUPA ce a intrebat. Vezi `asked`.
        */}
      {asked ? (
      <div className="two-lanes">
        {/*
          * Coloana din stanga: intai catalogul, apoi articolele.
           *
           * Ce s-a GASIT sta primul, si formularul al doilea. Ordinea de citire
           * e ordinea in care vrem sa se hotarasca omul: intai vede daca se
           * descurca singur, si abia daca nu, scrie un tichet. Invers, ochiul
           * cadea pe formular si restul devenea decor de langa el.
          *
          * Catalogul sus fiindca acolo e ceva ce se poate CERE pe loc, cu un
          * singur clic; articolul cere citit si facut. Cand amandoua se
          * potrivesc, prima e cea mai scurta.
          *
          * Grupele sunt SEPARATE si etichetate, nu o lista ordonata dupa scor:
          * catalogul raspunde la "vreau sa cer ceva", articolele la "ceva nu
          * merge". Amestecate, omul apasa pe un articol crezand ca a cerut un
          * laptop.
          */}
        <div className="two-lanes__found">
          {fromArticle > 0 ? (
            <p className="selfserve__note">
              {t('You came from the article')}{' '}
              <Link to={`/help/${fromArticle}`}>{initialQuery !== '' ? initialQuery : t('Help')}</Link>
            </p>
          ) : null}

          {search.services.length > 0 ? (
            <section className="selfserve__group">
              <h2 className="selfserve__title">{t('Request from the catalog')}</h2>
              <div className="stack">
                {search.services.map((item) => (
                  <ServiceRow key={item.id} item={item} searchId={search.searchId} />
                ))}
              </div>
            </section>
          ) : null}

          {otherArticles.length > 0 ? (
            <section className="selfserve__group">
              <h2 className="selfserve__title">{t('Solve it yourself')}</h2>
              <div className="stack">
                {otherArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} searchId={search.searchId} />
                ))}
              </div>
            </section>
          ) : null}

          {/* Cand n-a gasit nimic, coloana spune de ce e goala. Un spatiu alb
              langa un formular s-ar citi ca ceva care n-a apucat sa se incarce. */}
          {nothingFound ? (
            <p className="selfserve__note">
              {t('Nothing matched what you wrote. The form next to this goes to the service desk.')}
            </p>
          ) : null}
        </div>

        <div className="two-lanes__form">
      {types.status === 'loading' ? <Loading label={t('Loading ticket types…')} /> : null}
      {types.status === 'error' && types.error ? (
        <ErrorState error={types.error} onRetry={types.reload} />
      ) : null}

      {types.status === 'ready' && types.data ? (
        types.data.types.length === 0 ? (
          /* Nicio vina a omului si nimic de reincercat: e o configurare care
             lipseste la furnizor. Se spune, nu se arata un formular care ar
             esua la trimitere. */
          <EmptyState
            title={t('No ticket types available')}
            text={t('Direct tickets are not configured yet. Contact the service desk.')}
            action={
              <Link className="btn btn--primary" to="/catalog">{t('Browse the catalog')}</Link>
            }
          />
        ) : (
          <div className="panel panel--form">
            <form className="side-form" onSubmit={submit}>
              {/*
                * Antetul spune ce se intampla acum, nu ce ecran e asta.
                *
                * Cand ajunge aici, omul a scris deja o data ce vrea si n-a
                * gasit nimic. "Deschide un tichet nou" l-ar pune sa reia de la
                * capat; asta ii spune ca cererea lui e deja pe drum si mai are
                * de adaugat un singur lucru.
                */}
              <p className="form-lead">
                <strong>{t('Log an IT Support request')}</strong>
                {fromArticle > 0 ? (
                  <span className="form-lead__why">
                    {' '}{t('The article did not solve it, so this goes to the service desk.')}
                  </span>
                ) : nothingFound ? (
                  <span className="form-lead__why">
                    {' '}{t('Nothing we have matches, so this goes to the service desk.')}
                  </span>
                ) : null}
              </p>

              {submitError ? <InlineAlert>{submitError}</InlineAlert> : null}

              {/*
                * Tipul si subiectul: precompletate, dar VIZIBILE.
                *
                * Omul mai are de scris un singur lucru - descrierea - iar
                * celelalte doua sunt deja completate din ce a scris sus. Le
                * arat totusi, pe un rand mic: ce pleaca pe tichet trebuie sa se
                * vada. Ascunse, ar fi insemnat sa trimitem in numele lui un
                * subiect si un tip pe care nu le-a citit niciodata.
                */}
              <div className="prefilled">
                <div className="field field--slim">
                  <label className="field__label" htmlFor="ticket-type">
                    {t('Ticket type')}
                  </label>
                  <select
                    id="ticket-type"
                    className="field__control"
                    required
                    aria-required="true"
                    aria-invalid={fieldErrors.type_id ? true : undefined}
                    value={typeId}
                    onChange={(event) => {
                      setTypeTouched(true);
                      setTypeId(event.target.value);
                      if (fieldErrors.type_id) setFieldErrors(({ type_id: _, ...rest }) => rest);
                    }}
                  >
                    <option value="">{t('Choose a type')}</option>
                    {types.data.types.map((type) => (
                      <option key={type.id} value={String(type.id)}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.type_id ? (
                    <p className="field__hint field__hint--error" role="alert">{fieldErrors.type_id}</p>
                  ) : null}
                </div>

                <div className="field field--grow">
                  <label className="field__label" htmlFor="ticket-subject">
                    {t('Subject')}
                  </label>
                  <input
                    id="ticket-subject"
                    className="field__control"
                    type="text"
                    maxLength={255}
                    required
                    aria-required="true"
                    aria-invalid={fieldErrors.subject ? true : undefined}
                    placeholder={t('A few words about what you need.')}
                    value={subject}
                    onChange={(event) => {
                      setSubjectTouched(true);
                      setSubject(event.target.value);
                      if (fieldErrors.subject) setFieldErrors(({ subject: _, ...rest }) => rest);
                    }}
                  />
                  {fieldErrors.subject ? (
                    <p className="field__hint field__hint--error" role="alert">{fieldErrors.subject}</p>
                  ) : null}
                </div>
              </div>

              <div className="field">
                <label className="field__label" htmlFor="ticket-description">
                  {t('Description')}<span aria-hidden="true">*</span>
                </label>
                <textarea
                  id="ticket-description"
                  className="field__control"
                  rows={6}
                  required
                  aria-required="true"
                  /*
                   * FARA autoFocus.
                   *
                   * Il avea, de pe vremea cand formularul era deja pe ecran la
                   * incarcare. Acum se monteaza la ~400ms dupa ce omul se
                   * opreste din scris - iar daca se opreste ca sa se gandeasca
                   * si apoi continua, focusul i-ar sari din caseta in descriere
                   * si restul propozitiei ar ajunge in alt camp.
                   */
                  aria-invalid={fieldErrors.description ? true : undefined}
                  aria-describedby="ticket-description-help"
                  placeholder={t('What happened, what you need, since when.')}
                  value={description}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    if (fieldErrors.description) setFieldErrors(({ description: _, ...rest }) => rest);
                  }}
                />
                <p className="field__hint" id="ticket-description-help">
                  {t('Goes on the ticket, for whoever picks it up.')}
                </p>
                {fieldErrors.description ? (
                  <p className="field__hint field__hint--error" role="alert">{fieldErrors.description}</p>
                ) : null}
              </div>

              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {submitting ? t('Opening…') : t('Open ticket')}
              </button>
            </form>
          </div>
        )
      ) : null}
        </div>

      </div>
      ) : null}
    </>
  );
}
